/**
 * Landing Page Copy (M-PILOT-06)
 *
 * FI-tenant-scoped copy for the anonymous FI-referral landing /start/<handle>.
 * Eyebrow + headline + body. On save, platform-admin-api persists to the
 * Institution row AND pushes the copy to the consumer FiTenant via the
 * fi-policy-sync internal channel, so the public landing renders it.
 *
 * Backend: platform-admin-api routes mounted at
 *   GET /api/admin/tenants/me/landing-copy
 *   PUT /api/admin/tenants/me/landing-copy
 *
 * Types: PlatformAdmin.components.schemas.{LandingCopyConfig,LandingCopyConfigUpdate}
 * from `@aaimplatform/institutional-types`. No inline interfaces.
 */
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, Loader2, RotateCcw, Save } from 'lucide-react';
import { Button, Heading, Skeleton } from '@aaimplatform/ui';
import type { components } from '@aaimplatform/institutional-types/platform-admin';
import { platformAdminClient } from '../../api/client';

type LandingCopyConfig = components['schemas']['LandingCopyConfig'];
type LandingCopyConfigUpdate = components['schemas']['LandingCopyConfigUpdate'];

const QUERY_KEY = ['admin', 'tenants', 'me', 'landing-copy'] as const;
const EYEBROW_MAX = 120;
const HEADLINE_MAX = 160;
const BODY_MAX = 600;

interface FormState {
  landingEyebrow: string;
  landingHeadline: string;
  landingBody: string;
  connectEyebrow: string;
  connectHeadline: string;
  connectBody: string;
  connectInstitutionSearchTerm: string;
}

const EMPTY_FORM: FormState = {
  landingEyebrow: '', landingHeadline: '', landingBody: '',
  connectEyebrow: '', connectHeadline: '', connectBody: '',
  connectInstitutionSearchTerm: '',
};

function formFromConfig(config: LandingCopyConfig): FormState {
  return {
    landingEyebrow: config.landingEyebrow ?? '',
    landingHeadline: config.landingHeadline ?? '',
    landingBody: config.landingBody ?? '',
    connectEyebrow: config.connectEyebrow ?? '',
    connectHeadline: config.connectHeadline ?? '',
    connectBody: config.connectBody ?? '',
    connectInstitutionSearchTerm: config.connectInstitutionSearchTerm ?? '',
  };
}

function buildUpdate(form: FormState, baseline: LandingCopyConfig | undefined): LandingCopyConfigUpdate {
  const base = baseline ? formFromConfig(baseline) : EMPTY_FORM;
  const update: LandingCopyConfigUpdate = {};
  (Object.keys(form) as (keyof FormState)[]).forEach((k) => {
    if (form[k] !== base[k]) {
      update[k] = form[k].length === 0 ? null : form[k];
    }
  });
  return update;
}

export function LandingPageCopyPage() {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [saveErrorMessage, setSaveErrorMessage] = useState<string | null>(null);

  const copyQuery = useQuery({
    queryKey: QUERY_KEY,
    queryFn: async (): Promise<LandingCopyConfig> => {
      const { data, error } = await platformAdminClient.GET('/tenants/me/landing-copy');
      if (error || !data) throw new Error('Failed to load landing copy configuration');
      return data;
    },
    staleTime: 30_000,
  });

  useEffect(() => {
    if (copyQuery.data) {
      setForm(formFromConfig(copyQuery.data));
    }
  }, [copyQuery.data]);

  const isDirty = useMemo(() => {
    if (!copyQuery.data) return false;
    return Object.keys(buildUpdate(form, copyQuery.data)).length > 0;
  }, [form, copyQuery.data]);

  const saveMutation = useMutation({
    mutationFn: async (): Promise<LandingCopyConfig> => {
      const body = buildUpdate(form, copyQuery.data);
      const { data, error } = await platformAdminClient.PUT('/tenants/me/landing-copy', { body });
      if (error || !data) throw new Error('Failed to save landing copy configuration');
      return data;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(QUERY_KEY, data);
      setForm(formFromConfig(data));
      setSaveErrorMessage(null);
    },
    onError: (error: Error) => {
      setSaveErrorMessage(error.message);
    },
  });

  const setField = (key: keyof FormState, value: string, max: number) => {
    const next = value.length > max ? value.slice(0, max) : value;
    setForm((prev) => ({ ...prev, [key]: next }));
  };

  const handleReset = () => {
    if (copyQuery.data) setForm(formFromConfig(copyQuery.data));
    setSaveErrorMessage(null);
  };

  const canSave = isDirty && !saveMutation.isPending;

  if (copyQuery.isLoading) {
    return (
      <div>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-6">
          <Heading level={2}>Landing Page Copy</Heading>
          <div className="flex flex-wrap items-center gap-3">
            <Skeleton className="h-10 w-24" />
            <Skeleton className="h-10 w-36" />
          </div>
        </div>
        <Skeleton className="h-40 rounded-sm mb-6" />
        <Skeleton className="h-64 rounded-sm" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between mb-6 sm:gap-6">
        <div>
          <Heading level={2}>Landing Page Copy</Heading>
          <div className="text-sm font-sans text-tertiary leading-normal mt-2 max-w-2xl">
            Customize the eyebrow, headline, and body shown to members arriving on
            your branded Aaim landing page (the link you share to invite members to
            connect their portfolios). Leave a field blank to use the Aaim default.
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3 flex-shrink-0">
          <Button variant="secondary" onClick={handleReset} disabled={!isDirty || saveMutation.isPending}>
            <RotateCcw className="w-4 h-4" />
            Reset
          </Button>
          <Button variant="primary" onClick={() => saveMutation.mutate()} disabled={!canSave}>
            {saveMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {saveMutation.isPending ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>

      {(copyQuery.error || saveErrorMessage) && (
        <div className="bg-error/5 border border-error/20 rounded-sm p-4 mb-6 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-error flex-shrink-0" />
          <div className="text-sm font-sans text-error">
            {copyQuery.error ? 'Failed to load landing copy configuration' : saveErrorMessage}
          </div>
        </div>
      )}

      {/* Screen 1 -- Get Started landing */}
      <div className="bg-surface rounded-sm shadow-sm p-6 mb-6">
        <Heading level={3} className="mb-1">Screen 1 -- Welcome (Get Started)</Heading>
        <div className="text-sm font-sans text-tertiary leading-normal mb-4">
          The first screen members see. They click Get Started to continue.
        </div>
        <CopyField label="Eyebrow" value={form.landingEyebrow} max={EYEBROW_MAX} warn={20}
          placeholder="Your Credit Union + Aaim" onChange={(v) => setField('landingEyebrow', v, EYEBROW_MAX)} />
        <CopyField label="Headline" value={form.landingHeadline} max={HEADLINE_MAX} warn={20}
          placeholder="Borrow against your whole portfolio" onChange={(v) => setField('landingHeadline', v, HEADLINE_MAX)} />
        <CopyField label="Body" value={form.landingBody} max={BODY_MAX} warn={60} multiline
          placeholder="Unlock lending against investments you already hold -- without selling them." onChange={(v) => setField('landingBody', v, BODY_MAX)} />
      </div>

      {/* Screen 2 -- connect-accounts step */}
      <div className="bg-surface rounded-sm shadow-sm p-6">
        <Heading level={3} className="mb-1">Screen 2 -- Connect accounts</Heading>
        <div className="text-sm font-sans text-tertiary leading-normal mb-4">
          Shown after Get Started, where members securely connect their accounts.
        </div>
        <CopyField label="Eyebrow" value={form.connectEyebrow} max={EYEBROW_MAX} warn={20}
          placeholder="Your Credit Union + Aaim" onChange={(v) => setField('connectEyebrow', v, EYEBROW_MAX)} />
        <CopyField label="Headline" value={form.connectHeadline} max={HEADLINE_MAX} warn={20}
          placeholder="Connect your accounts" onChange={(v) => setField('connectHeadline', v, HEADLINE_MAX)} />
        <CopyField label="Body" value={form.connectBody} max={BODY_MAX} warn={60} multiline
          placeholder="Your credentials go directly to your institution -- Aaim never sees them." onChange={(v) => setField('connectBody', v, BODY_MAX)} />
        <CopyField label="Institution search prefill" value={form.connectInstitutionSearchTerm} max={EYEBROW_MAX} warn={20}
          placeholder="Your institution" onChange={(v) => setField('connectInstitutionSearchTerm', v, EYEBROW_MAX)} />
        <div className="text-xs font-sans text-tertiary leading-normal -mt-2 mb-4 max-w-2xl">
          Pre-populates the institution search box in the connect widget so members
          find your institution faster. Members can still search for another. Leave
          blank for no prefill.
        </div>
      </div>

    </div>
  );
}

interface CopyFieldProps {
  label: string;
  value: string;
  max: number;
  warn: number;
  placeholder: string;
  multiline?: boolean;
  onChange: (value: string) => void;
}

function CopyField({ label, value, max, warn, placeholder, multiline, onChange }: CopyFieldProps) {
  const remaining = max - value.length;
  return (
    <div className="mb-4 last:mb-0">
      <div className="flex items-center justify-between mb-2">
        <label className="text-sm font-sans font-medium text-primary leading-normal">{label}</label>
        <div className={`text-xs font-sans ${remaining < warn ? 'text-error' : 'text-tertiary'}`}>
          {remaining} characters remaining
        </div>
      </div>
      {multiline ? (
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          rows={4}
          maxLength={max}
          className="w-full px-3 py-2 border border-recessed rounded-sm text-sm font-sans focus:outline-none focus:border-accent resize-y"
        />
      ) : (
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          maxLength={max}
          className="w-full px-3 py-2 border border-recessed rounded-sm text-sm font-sans focus:outline-none focus:border-accent"
        />
      )}
    </div>
  );
}
